/**
 * useCamera — barrel exports
 *
 * Re-exports the public API of the useCamera module so consumers can import
 * from `@/hooks/useCamera` (which resolves to this directory's index).
 *
 * Public API:
 *   - useCamera (default + named)
 *   - CameraModal (named)
 *   - DocumentCameraModal (named) — receipt/document capture preset
 *   - VehicleCameraModal (named) — vehicle photo capture preset
 *   - SimpleCameraModal (named) — basic photo capture preset
 *   - BarcodeScannerModal (named) — barcode scanning modal
 *   - useTorchMode (named) — 3-state torch mode feature hook
 *   - Types: CameraFacingMode, CropRect, CameraModalProps, CaptureShortcut,
 *     CaptureModeButton, UseCameraConfig, UseCameraReturn, BarcodeFormat, etc.
 */

// Core hook + modal
export { useCamera } from './useCamera';
export { default as CameraModal } from './CameraModal';

// Preset wrapper components
export { default as DocumentCameraModal } from './DocumentCameraModal';
export type { DocumentCameraModalProps } from './DocumentCameraModal';
export { default as VehicleCameraModal } from './VehicleCameraModal';
export type { VehicleCameraModalProps } from './VehicleCameraModal';
export { default as SimpleCameraModal } from './SimpleCameraModal';
export type { SimpleCameraModalProps } from './SimpleCameraModal';

// Barcode scanner
export { default as BarcodeScannerModal } from './BarcodeScannerModal';
export type { BarcodeScannerModalProps, BarcodeScanResult } from './BarcodeScannerModal';
export type { BarcodeFormat } from './barcodeTypes';

// Feature hooks
export { useTorchMode } from './useTorchMode';
export type { TorchMode, UseTorchModeParams, UseTorchModeReturn } from './useTorchMode';
export { usePdf417Detection } from './usePdf417Detection';
export type { UsePdf417DetectionParams } from './usePdf417Detection';

// Internal component types
export type { CameraActionButtonProps } from './CameraActionButton';

// Guide silhouettes
export { default as LicenseDiskSilhouette } from './LicenseDiskSilhouette';

// Core types
export type {
  CameraFacingMode,
  CameraFacingBucket,
  CameraFillLightMode,
  CameraFocusMode,
  CropRect,
  CameraDeviceOption,
  CameraPointOfInterest,
  ExtendedMediaTrackCapabilities,
  ExtendedMediaTrackConstraintSet,
  ExtendedMediaTrackConstraints,
  ExtendedMediaTrackSettings,
  ImageCaptureLike,
  ImageCaptureConstructor,
  WindowWithImageCapture,
  UseCameraConfig,
  UseCameraReturn,
  CameraModalProps,
  CaptureShortcut,
  CaptureModeButton,
} from './types';

// Default export for backwards compatibility with `import useCamera from '@/hooks/useCamera'`
export { default } from './useCamera';
